"use client";

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getNavItems } from "../../../_lib/routes";

import Link from "next/link";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useSocialMediaData, useCompanyData } from "../../../_hooks/useSanityData";

type MobileMenuProps = {
	isOpen: boolean;
	onClose: () => void;
};

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
	const navItems = getNavItems();
	const pathname = usePathname();
	const { socialMedia } = useSocialMediaData();
	const { companyInfo } = useCompanyData();

	const socialMediaPairs = [];
	for (let i = 0; i < socialMedia.length; i += 2) {
		socialMediaPairs.push(socialMedia.slice(i, i + 2));
	}

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscape);
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}

		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.classList.remove("overflow-hidden");
		};
	}, [isOpen, onClose]);

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: "100svh", opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{
						duration: 0.4,
						ease: [0.4, 0.0, 0.2, 1],
						height: { duration: 0.5 },
					}}
					className="overflow-hidden"
				>
					<div className="flex h-full flex-col px-5 pt-16 pb-5">
						<nav aria-label="Main navigation">
							<ul className="">
								{navItems.map((item) => {
									const isActive = pathname === item.href;

									return (
										<li key={item.href} className="border-graphite-black-middle border-t py-5">
											<Link
												key={item.href}
												href={item.href}
												onClick={onClose}
												className={clsx(
													"block transition-colors duration-200",
													isActive
														? "text-graphite-black text-cap-m-active"
														: "text-graphite-black/50 hover:text-graphite-black text-cap-m",
												)}
											>
												{item.label}
											</Link>
										</li>
									);
								})}
							</ul>
						</nav>

						<div className="border-graphite-black-middle mt-auto border-t pt-5 pb-20">
							<div className="grid grid-cols-2 gap-x-2.5">
								<div className="col-span-2 w-10/12">
									<div className="mb-5 flex flex-col gap-y-2">
										{companyInfo.phoneNumber && (
											<p className="text-cap-m text-graphite-black/50 hover:text-graphite-black active:text-graphite-black transition-colors duration-300">
												<a href={`tel:${companyInfo.phoneNumber}`}>{companyInfo.phoneNumber}</a>
											</p>
										)}
										{companyInfo.email && (
											<p className="text-cap-m text-graphite-black/50 hover:text-graphite-black active:text-graphite-black transition-colors duration-300">
												<a href={`mailto:${companyInfo.email}`}>{companyInfo.email}</a>
											</p>
										)}
									</div>
									<div className="flex flex-col gap-y-2.5">
										{socialMediaPairs.map((pair, rowIndex) => (
											<p key={rowIndex} className="text-cap-m">
												{pair.map((social, index) => (
													<span key={social._id}>
														<a
															href={social.link}
															target="_blank"
															rel="noopener noreferrer"
															className="text-graphite-black/50 hover:text-graphite-black active:text-graphite-black transition-colors duration-300"
														>
															{social.name}
														</a>
														{index < pair.length - 1 && <span className="text-graphite-black/50"> / </span>}
													</span>
												))}
											</p>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
